import { X } from "lucide-react";
import { AttachmentItem } from "../../hooks/useAttachments";

interface AttachmentChipProps {
  attachment: AttachmentItem;
  onRemove: (id: string) => void;
}

function getExtension(name: string) {
  const parts = name.split(".");
  if (parts.length < 2) return "FILE";
  return parts.pop()?.toUpperCase() || "FILE";
}

export const AttachmentChip: React.FC<AttachmentChipProps> = ({
  attachment,
  onRemove,
}) => {
  const { id, name, status, progress, warning, error } = attachment;
  const isError = status === "error";
  const isPending = status === "pending";
  const extension = getExtension(name);

  return (
    <div
      role="listitem"
      aria-label={`${name} attachment`}
      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl border bg-[#F3ECE4] dark:bg-[#2C405F] shadow-sm transition hover:shadow-md ${
        isError
          ? "border-[#d6615c] dark:border-[#d6615c]"
          : "border-[#E5DDD3] dark:border-[#455D7D]"
      }`}
      style={{ minWidth: "200px", maxWidth: "280px" }}
    >
      {/* File icon */}
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FAF6F1] dark:bg-[#6E829E] flex-shrink-0">
        <svg
          className="h-5 w-5 text-[#2C405F] dark:text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1D1B19] dark:text-[#E3E6EA] truncate">
          {name}
        </p>
        <p className="text-xs text-[#8E8378] dark:text-[#9AAABF] uppercase tracking-wide">
          {extension}
        </p>
      </div>

      {/* Remove button - always visible */}
      <button
        type="button"
        className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E5DDD3] dark:bg-[#455D7D] text-[#8E8378] dark:text-[#9AAABF] hover:bg-[#D3C8BB] dark:hover:bg-[#6E829E] transition z-10"
        onClick={() => onRemove(id)}
        aria-label={`Remove attachment ${name}`}
      >
        <X size={12} />
      </button>

      {/* Progress bar */}
      {isPending && (
        <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-[#E5DDD3] dark:bg-[#455D7D] rounded-b-xl">
          <div
            className="h-full bg-[#2C405F] dark:bg-[#6E829E] transition-all"
            style={{ width: `${progress}%` }}
            aria-label="Reading attachment"
          />
        </div>
      )}

      {/* Error/Warning badge */}
      {(warning || error) && (
        <div
          className={`absolute -top-1.5 -right-1.5 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide shadow-sm ${
            error ? "bg-[#EF4444] text-white" : "bg-[#F59E0B] text-white"
          }`}
        >
          {error ? "Error" : "Warning"}
        </div>
      )}
    </div>
  );
};
